/**
 * File: orderPage.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v1.2.0
 * Created: 2026-04-25
 * Last Updated: 2026-07-25
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Local project files
import { countMatchingRows } from "../../utils/tableUtilities";
import { updateEstimateStatus } from "../../utils/ServiceRequests";

// Icons
import { CiSearch, CiSquarePlus } from "react-icons/ci";
import { MdFilterList } from "react-icons/md";
import { TbTableExport } from "react-icons/tb";

// Order Page
export const orderPage = {
    meta: {
        id: "order-page",
    },
    statCards: {
        estimatesTable: [
            {
                id: "stat-total-estimates",
                label: "Total Estimates",
                value: (rows) => rows.length,
            },
            {
                id: "stat-accepted-estimates",
                label: "Accepted Estimates",
                value: (rows) =>
                    countMatchingRows(rows, "estimate_status", "accepted"),
            },
            {
                id: "stat-pending-estimates",
                label: "Pending Estimates",
                value: (rows) =>
                    countMatchingRows(rows, "estimate_status", "sent"),
            },
            {
                id: "stat-rejected-estimates",
                label: "Rejected Estimates",
                value: (rows) =>
                    countMatchingRows(rows, "estimate_status", "rejected"),
            },
        ],
        workOrdersTable: [
            {
                id: "stat-total-work-orders",
                label: "Total Work Orders",
                value: (rows) => rows.length,
            },
            {
                id: "stat-completed-work-orders",
                label: "Completed Work Orders",
                value: (rows) =>
                    countMatchingRows(rows, "work_order_status", "Completed"),
            },
            {
                id: "stat-progress-work-orders",
                label: "In Progress Work Orders",
                value: (rows) =>
                    countMatchingRows(rows, "work_order_status", "In Progress"),
            },
            {
                id: "stat-scheduled-work-orders",
                label: "Scheduled Work Orders",
                value: (rows) =>
                    countMatchingRows(rows, "work_order_status", "Scheduled"),
            },
        ],
        invoicesTable: [
            {
                id: "stat-total-invoices",
                label: "Total Invoices",
                value: (rows) => rows.length,
            },
            {
                id: "stat-completed-invoices",
                label: "Completed Invoices",
                value: (rows) =>
                    countMatchingRows(rows, "invoice_status", "Completed"),
            },
            {
                id: "stat-progress-invoices",
                label: "In Progress Invoices",
                value: (rows) =>
                    countMatchingRows(rows, "invoice_status", "In Progress"),
            },
            {
                id: "stat-scheduled-invoices",
                label: "Scheduled Invoices",
                value: (rows) =>
                    countMatchingRows(rows, "invoice_status", "Scheduled"),
            },
        ],
    },
    tableLayout: {
        estimatesTable: {
            id: "estimates-table",
            primaryKey: "estimate_id",
            tableControls: {
                toggleButtons: [
                    {
                        id: "toggle-estimates-table",
                        text: "Estimates",
                        state: "estimates-table",
                    },
                    {
                        id: "toggle-work-orders-table",
                        text: "Work Orders",
                        state: "work-orders-table",
                    },
                    {
                        id: "toggle-invoices-table",
                        text: "Invoices",
                        state: "invoices-table",
                    },
                ],
                iconButtons: {
                    add: { icon: CiSquarePlus, size: 24 },
                    search: { icon: CiSearch, size: 24 },
                    export: { icon: TbTableExport, size: 24 },
                },
                filterButton: {
                    iconButton: {
                        icon: MdFilterList,
                        size: 24,
                    },
                    filterModal: {
                        filters: [
                            {
                                id: "estimate_status",
                                type: "dropDownField",
                                label: "Status",
                                options: [
                                    { id: "pending", value: "pending" },
                                    { id: "accepted", value: "accepted" },
                                    { id: "rejected", value: "rejected" },
                                    { id: "expired", value: "expired" },
                                ],
                            },
                            {
                                id: "service_package",
                                type: "dropDownField",
                                label: "Package",
                                options: [
                                    { id: "basic", value: "basic" },
                                    { id: "standard", value: "standard" },
                                    { id: "premium", value: "premium" },
                                ],
                            },
                        ],
                        textButtons: {
                            apply: {
                                text: "Apply",
                                type: "primary",
                            },
                            clear: {
                                text: "Clear",
                                type: "secondary",
                            },
                        },
                    },
                },
            },
            headers: {
                estimateId: {
                    key: "estimate_id",
                    label: "Estimate #",
                    unit: "-",
                    type: "id",
                },
                accountId: {
                    key: "account_id",
                    label: "Account #",
                    unit: "-",
                    type: "id",
                },
                companyName: {
                    key: "company_name",
                    label: "Company",
                    unit: "-",
                    type: "text",
                },
                firstName: {
                    key: "first_name",
                    label: "First Name",
                    unit: "-",
                    type: "text",
                },
                lastName: {
                    key: "last_name",
                    label: "Last Name",
                    unit: "-",
                    type: "text",
                },
                phoneNumber: {
                    key: "phone_number",
                    label: "Phone Number",
                    unit: "-",
                    type: "text",
                },
                email: {
                    key: "email",
                    label: "Email",
                    unit: "-",
                    type: "text",
                },
                services: {
                    key: "services",
                    label: "Services",
                    unit: "-",
                    type: "array",
                },
                disciplines: {
                    key: "disciplines",
                    label: "Disciplines",
                    unit: "-",
                    type: "array",
                },
                servicePackage: {
                    key: "service_package",
                    label: "Package",
                    unit: "-",
                    type: "text",
                },
                timeline: {
                    key: "timeline",
                    label: "Timeline",
                    unit: "-",
                    type: "text",
                },
                projectCity: {
                    key: "project_city",
                    label: "City",
                    unit: "-",
                    type: "text",
                },
                projectState: {
                    key: "project_state",
                    label: "State",
                    unit: "-",
                    type: "textCenter",
                },
                projectType: {
                    key: "project_type",
                    label: "Project Type",
                    unit: "-",
                    type: "text",
                },
                projectSize: {
                    key: "project_size",
                    label: "Project Size",
                    unit: "-",
                    type: "textRight",
                },
                projectScope: {
                    key: "project_scope",
                    label: "Project Scope",
                    unit: "-",
                    type: "text",
                },
                projectFiles: {
                    key: "project_files",
                    label: "Files",
                    unit: "-",
                    type: "array",
                },
                estimateStatus: {
                    key: "estimate_status",
                    label: "Status",
                    unit: "-",
                    type: "textCenter",
                },
                estimateAmount: {
                    key: "estimate_amount",
                    label: "Estimate Amount",
                    unit: "$",
                    type: "currency",
                },
                consentGiven: {
                    key: "consent_given",
                    label: "Consent Given",
                    unit: "-",
                    type: "boolean",
                },
                consentTimestamp: {
                    key: "consent_timestamp",
                    label: "Consent Timestamp",
                    unit: "-",
                    type: "datetime",
                },
                consentText: {
                    key: "consent_text",
                    label: "Consent Text",
                    unit: "-",
                    type: "text",
                },
                consentIp: {
                    key: "consent_ip",
                    label: "Consent IP",
                    unit: "-",
                    type: "ipAddress",
                },
                consentUserAgent: {
                    key: "consent_user_agent",
                    label: "User Agent",
                    unit: "-",
                    type: "text",
                },
                createdAt: {
                    key: "created_at",
                    label: "Created At",
                    unit: "-",
                    type: "datetime",
                },
                expiresAt: {
                    key: "expires_at",
                    label: "Expires At",
                    unit: "-",
                    type: "datetime",
                },
                updatedAt: {
                    key: "updated_at",
                    label: "Updated At",
                    unit: "-",
                    type: "datetime",
                },
            },
            rowControls: [
                {
                    type: "text",
                    text: "Approve",
                    onClick: (estimateID) =>
                        updateEstimateStatus(estimateID, "accepted"),
                },
                {
                    type: "text",
                    text: "Reject",
                    onClick: (estimateID) =>
                        updateEstimateStatus(estimateID, "rejected"),
                },
            ],
        },
        workOrdersTable: {
            id: "work-orders-table",
            primaryKey: "work_order_id",
            tableControls: {
                toggleButtons: [
                    {
                        id: "toggle-estimates-table",
                        text: "Estimates",
                        state: "estimates-table",
                    },
                    {
                        id: "toggle-work-orders-table",
                        text: "Work Orders",
                        state: "work-orders-table",
                    },
                    {
                        id: "toggle-invoices-table",
                        text: "Invoices",
                        state: "invoices-table",
                    },
                ],
                iconButtons: {
                    add: { icon: CiSquarePlus, size: 24 },
                    filter: { icon: MdFilterList, size: 24 },
                    search: { icon: CiSearch, size: 24 },
                    export: { icon: TbTableExport, size: 24 },
                },
                filterButton: {
                    iconButton: {
                        icon: MdFilterList,
                        size: 24,
                    },
                    filterModal: {
                        filters: [
                            {
                                id: "work_order_status",
                                type: "dropDownField",
                                label: "Status",
                                options: [
                                    { id: "pending", value: "pending" },
                                    { id: "in progress", value: "in progress" },
                                    { id: "completed", value: "completed" },
                                    { id: "cancelled", value: "cancelled" },
                                ],
                            },
                        ],
                        textButtons: {
                            apply: {
                                text: "Apply",
                                type: "primary",
                            },
                            clear: {
                                text: "Clear",
                                type: "secondary",
                            },
                        },
                    },
                },
            },
            headers: {
                workOrderId: {
                    key: "work_order_id",
                    label: "Order #",
                    unit: "-",
                    type: "id",
                },
                estimateId: {
                    key: "estimate_id",
                    label: "Estimate #",
                    unit: "-",
                    type: "id",
                },
                workOrderStatus: {
                    key: "work_order_status",
                    label: "Status",
                    unit: "-",
                    type: "textCenter",
                },
                createdAt: {
                    key: "created_at",
                    label: "Created At",
                    unit: "-",
                    type: "datetime",
                },
                updatedAt: {
                    key: "updated_at",
                    label: "Updated At",
                    unit: "-",
                    type: "datetime",
                },
            },
            rowControls: [{ type: "text", text: "Download" }],
        },
        invoicesTable: {
            id: "invoices-table",
            primaryKey: "invoice_id",
            tableControls: {
                toggleButtons: [
                    {
                        id: "toggle-estimates-table",
                        text: "Estimates",
                        state: "estimates-table",
                    },
                    {
                        id: "toggle-work-orders-table",
                        text: "Work Orders",
                        state: "work-orders-table",
                    },
                    {
                        id: "toggle-invoices-table",
                        text: "Invoices",
                        state: "invoices-table",
                    },
                ],
                iconButtons: {
                    add: { icon: CiSquarePlus, size: 24 },
                    filter: { icon: MdFilterList, size: 24 },
                    search: { icon: CiSearch, size: 24 },
                    export: { icon: TbTableExport, size: 24 },
                },
                filterButton: {
                    iconButton: {
                        icon: MdFilterList,
                        size: 24,
                    },
                    filterModal: {
                        filters: [],
                        textButtons: {
                            apply: {
                                text: "Apply",
                                type: "primary",
                            },
                            clear: {
                                text: "Clear",
                                type: "secondary",
                            },
                        },
                    },
                },
            },
            headers: {
                invoiceId: {
                    key: "invoice_id",
                    label: "Invoice #",
                    unit: "-",
                    type: "id",
                },
                workOrderId: {
                    key: "work_order_id",
                    label: "Work Order #",
                    unit: "-",
                    type: "id",
                },
                createdAt: {
                    key: "created_at",
                    label: "Created At",
                    unit: "-",
                    type: "datetime",
                },
                updatedAt: {
                    key: "updated_at",
                    label: "Updated At",
                    unit: "-",
                    type: "datetime",
                },
            },
            rowControls: [{ type: "text", text: "Download" }],
        },
    },
};
